import React from 'react';
import PropTypes from 'prop-types';
import theme from 'styles/themes/wikifactory';

import {
  ButtonStyled,
  IconStyled,
  SpinnerStyled,
  IconWrapper,
  IconNotificationBadge,
} from './Button.styled';

export const buttonAppearance = {
  DEFAULT: 'default',
  HERO: 'hero',
  PLAIN: 'plain',
};

export const buttonVariant = {
  PRIMARY: 'primary',
  SECONDARY: 'secondary',
  TERTIARY: 'tertiary',
  DESTRUCTIVE: 'destructive',
  SUCCESS: 'success',
  WARNING: 'warning',
  MINIMAL: 'minimal',
};

export const buttonSize = {
  XSMALL: 'xsmall',
  SMALL: 'small',
  DEFAULT: 'default',
  MEDIUM: 'medium',
  LARGE: 'large',
};

export const iconPosition = {
  LEFT: 'left',
  RIGHT: 'right',
};

export default function Button(props) {
  const {
    icon,
    text,
    iconViewBox,
    loading,
    appearance,
    size,
    removeBold,
    badgeValue,
    iconColor,
    variant,
  } = props;

  const iconComponent = icon && (
    <IconWrapper
      withText={text}
      iconPosition={
        props.iconPosition || (appearance === 'default' ? 'left' : 'right')
      }
      appearance={appearance}
    >
      <IconNotificationBadge value={badgeValue} withText={text}>
        <IconStyled
          name={icon}
          viewBox={iconViewBox}
          withText={text}
          size={size}
          colorPath={
            iconColor ||
            (variant === 'minimal' ? theme.color.wfBlack : 'currentColor')
          }
          iconPosition={
            iconPosition || (appearance === 'default' ? 'left' : 'right')
          }
          customStroke={
            appearance === 'plain' && !removeBold ? 'regular' : 'light'
          }
        />
      </IconNotificationBadge>
    </IconWrapper>
  );
  return (
    <ButtonStyled
      type='button'
      aria-label={props.ariaLabel}
      {...props}
      disabled={loading || props.disabled}
      loading={typeof loading === 'boolean' ? loading.toString() : loading}
    >
      {loading ? (
        <>
          <SpinnerStyled size='small' autoColor />
          {text}
        </>
      ) : (
        <>
          {iconComponent}
          {text}
        </>
      )}
    </ButtonStyled>
  );
}

Button.defaultProps = {
  appearance: buttonAppearance.DEFAULT,
  variant: buttonVariant.PRIMARY,
  disabled: false,
  size: buttonSize.DEFAULT,
  active: false,
  negativeVersion: false,
  removeBold: false,
  badgeValue: 0,
  shadow: false,
};

Button.propTypes = {
  text: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  /**
   * If Button doesn't include text, we should specify an aria-label attribute describing the button.
   */
  ariaLabel: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  /**
   * Defines different variants for the component
   */
  variant: PropTypes.oneOf(Object.values(buttonVariant)),
  /**
   * Defines appearances for the component based in the context where its shown
   */
  appearance: PropTypes.oneOf(Object.values(buttonAppearance)),
  icon: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  iconViewBox: PropTypes.string,
  shadow: PropTypes.bool,
  /**
   * This remove the default bold
   */
  removeBold: PropTypes.bool,
  /**
   * This prop allows to change the icon position inside button, between 'left' and 'right'.
   */
  iconPosition: PropTypes.oneOf(Object.values(iconPosition)),
  /**
   * This prop allows to rotate the icon horizontally.
   */
  iconRotateY: PropTypes.bool,
  /**
   * Disables user action and applies disabled styles on the Button
   */
  disabled: PropTypes.bool,
  /**
   * Will show a white version of the button for darkThemes
   */
  negativeVersion: PropTypes.bool,
  /**
   * Allows Button to grow to the width of its container
   */
  fullWidth: PropTypes.bool,
  /**
   * Shows Button with spinner replacing text while an action is performed
   */
  loading: PropTypes.bool,
  /**
   * Shows Button as active.
   */
  active: PropTypes.bool,
  // size: PropTypes.oneOf(Object.values(buttonSize)),
  size: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /**
   * The same as margin CSS property using a list of predefined sizes (theme.spacing). For example 'default 0' will be the same as '1rem 0'. The theme.spacing keys will be replaced by the correspondent value.
   */
  margin: PropTypes.string,
  /**
   * Custom color for text and border
   */
  customColor: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  /**
   * Custom color for background
   */
  customBgColor: PropTypes.string,
  /**
   * Will show the button without border-radius applied
   */
  square: PropTypes.bool,
  /**
   * Will make the background transparent
   */
  transparent: PropTypes.bool,
  /**
   * Will make it an outline
   */
  outline: PropTypes.bool,
  /**
   * Will show a Notification Badge on the icon
   */
  badgeValue: PropTypes.number,
  /**
   * Override icon color
   */
  iconColor: PropTypes.string,
  /**
   * Intrinsic onClick prop, here for typescript prop checking purposes.
   */
  onClick: PropTypes.func,
};
